import React, { useState, useEffect } from "react";
import {
  uploadModalImageService,
  getBannerImages,
  updateBannerImageService,
} from "../../../services/apiService";

/* ---------- Helper: File → Base64 ---------- */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const BannerUpload = () => {
  const [type, setType] = useState("Banner");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [banners, setBanners] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [modalFile, setModalFile] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const userid = 1; // replace with logged-in user id

  /* ---------- Fetch banners by type ---------- */
  const fetchBanners = async () => {
    try {
      const res = await getBannerImages(type);
      setBanners(res.Data || []);
    } catch (err) {
      console.error("Failed to fetch banners", err);
    }
  };

  useEffect(() => {
    fetchBanners();
    setPage(1); // reset to first page on type change
  }, [type]);

  const handleUpload = async () => {
    if (!file || !type) return alert("Select type & image");

    const formData = new FormData();
    formData.append("type", type);
    formData.append("image", file);
    formData.append("filename", file.name);
    formData.append("processes", "Banner");
    formData.append("userid", userid);

    try {
      setUploading(true);
      await uploadModalImageService(formData);
      alert("Banner uploaded successfully");
      setFile(null);
      fetchBanners();
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ---------- Open Edit Modal ---------- */
  const handleEdit = (banner) => {
    setEditingBanner({
      ...banner,
      active: banner.active ? 1 : 0,
    });
    setModalFile(null);
    setEditModalOpen(true);
  };

  /* ---------- Save Edit ---------- */
  const handleSaveEdit = async () => {
    if (!editingBanner) return;

    try {
      let base64 = null;
      if (modalFile) base64 = await fileToBase64(modalFile);

      const payload = {
        filename: modalFile ? modalFile.name : editingBanner.filename,
        image: base64,
        userid,
        processes: editingBanner.type,
        active: editingBanner.active ? 1 : 0,
      };

      await updateBannerImageService(editingBanner.id, payload);

      alert("Image updated successfully");
      setEditModalOpen(false);
      setEditingBanner(null);
      fetchBanners();
    } catch (err) {
      console.error(err);
      alert("Image not updated!");
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(banners.length / pageSize);
  const paginatedBanners = banners.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div style={{ padding: "20px", background: "#fff", borderRadius: "8px" }}>
      {/* ---------- TYPE SELECT ---------- */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "15px" }}>
        <label style={{ marginRight: "8px", fontWeight: 600 }}>Select Type of Banners:</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #ccc" }}
        >
          <option>Banner</option>
          <option>Brand</option>
          <option>Brand Banner</option>
          <option>SubCategory</option>
          <option>Sub Banner</option>
          <option>Category</option>
        </select>
      </div>

      {/* ---------- TABLE ---------- */}
      <h3>Data for Type: {type}</h3>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Type</th>
            <th>Image URL</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedBanners.map((banner) => (
            <tr key={banner.id} style={{ borderBottom: "1px solid #ccc" }}>
              <td>{banner.id}</td>
              <td>{banner.type}</td>
              <td>
                <a href={banner.imageurl} target="_blank" rel="noreferrer">
                  {banner.imageurl}
                </a>
              </td>
              <td>{banner.active ? "Active" : "Inactive"}</td>
              <td>
                <button
                  onClick={() => handleEdit(banner)}
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ---------- PAGINATION ---------- */}
      <div
        className="pagination"
        style={{
          marginTop: "15px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="items-per-page" style={{color:"black"}}>
          Items per page:
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            style={{ marginLeft: "5px" }}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div className="page-controls">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span style={{ margin: "0 10px" }}>
            {page} / {totalPages || 1}
          </span>
          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* ---------- EDIT MODAL ---------- */}
      {editModalOpen && editingBanner && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", width: "500px" }}>
            <h3>Edit Banner ID: {editingBanner.id}</h3>
            <label>Type</label>
            <input value={editingBanner.type} readOnly className="form-control mb-2" />

            <label>Active</label>
            <select
              value={editingBanner.active}
              onChange={(e) =>
                setEditingBanner({ ...editingBanner, active: Number(e.target.value) })
              }
              className="form-control mb-2"
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>

            <div style={{ marginBottom: "10px" }}>
              <label>Upload New Image (Optional):</label>
              <input type="file" accept="image/*" onChange={(e) => setModalFile(e.target.files[0])} />
              {modalFile && <div>Selected: {modalFile.name}</div>}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setEditModalOpen(false)}
                style={{
                  marginRight: "10px",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "none",
                  background: "#6b7280",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "none",
                  background: "#16a34a",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerUpload;
